import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getLogger } from "@hoteltradergit/ht-telemetry-ui-lib";
import { POWER_BI_ALLOWED_ROUTES } from "../constants";
import { usePowerBINavigationContext } from "../contexts/PowerBINavigationContext";

/**
 * Custom hook to handle PowerBI button click navigation
 * Uses PowerBINavigationContext for clean state management across hook instances
 * @param {object} report - PowerBI report object (can be null initially)
 * @returns {Array} - Additional event handlers array for usePowerBIEmbed
 */
const usePowerBIButtonNavigation = (report) => {
  const navigate = useNavigate();
  const {
    navigationTitle,
    reportRef,
    updateReport,
    triggerNavigation,
    resetNavigation,
  } = usePowerBINavigationContext();
  const handlersRef = useRef(null);

  // Update report reference in context when it changes
  useEffect(() => {
    updateReport(report);
  }, [report, updateReport]);

  // Extract alignment score from report
  const extractAlignmentScore = useCallback(async () => {
    const currentReport = reportRef.current;
    if (!currentReport) return 0;

    try {
      const pages = await currentReport.getPages();
      const activePage = pages.find((page) => page.isActive);
      if (activePage) {
        const visuals = await activePage.getVisuals();
        const scoreObj = visuals.find((visual) =>
          visual.title.startsWith("Score:")
        );
        if (scoreObj?.title) {
          const score = Number.parseFloat(scoreObj.title.split(":")[1]);
          return score || 0;
        }
      }
    } catch (error) {
      getLogger().error("usePowerBIButtonNavigation", {
        type: "Error",
        page_url: window.location.href,
        error_desc: "Error fetching visuals",
        error_stack: error?.stack ?? null,
      });
    }
    return 0;
  }, [reportRef]);

  // Handle button click navigation to promotions
  useEffect(() => {
    const handleButtonNavigation = async () => {
      if (!navigationTitle) return;

      const currentTitle = navigationTitle;
      resetNavigation();

      // If no report available, navigate without alignment score
      if (!reportRef.current) {
        navigate(currentTitle, {
          state: { alignmentScore: 0 },
        });
        return;
      }

      // Extract alignment score and navigate with it
      const alignmentScore = await extractAlignmentScore();
      navigate(currentTitle, {
        state: { alignmentScore: alignmentScore * 100 },
      });
    };

    if (navigationTitle) {
      handleButtonNavigation();
    }
  }, [
    navigationTitle,
    navigate,
    extractAlignmentScore,
    reportRef,
    resetNavigation,
  ]);

  // Create event handler array if it doesn't exist
  // Since triggerNavigation is stable (from context with useCallback),
  // we can safely create the handler once and reuse it
  if (!handlersRef.current) {
    handlersRef.current = [
      [
        "buttonClicked",
        (event) => {
          const { detail: { title: buttonTitle } = {} } = event;

          // Check if button title matches allowed routes
          if (
            buttonTitle &&
            (POWER_BI_ALLOWED_ROUTES.includes(buttonTitle) ||
              buttonTitle.startsWith("/promotions?pcode="))
          ) {
            // Trigger navigation through context
            triggerNavigation(buttonTitle);
          }
        },
      ],
    ];
  }

  return handlersRef.current;
};

export default usePowerBIButtonNavigation;
